import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  TablePagination,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Visit, Patient } from '../types';

const Visits: React.FC = () => {
  const navigate = useNavigate();
  const [visits, setVisits] = useState<Visit[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingVisit, setEditingVisit] = useState<Visit | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalVisits, setTotalVisits] = useState(0);

  // Form state
  const [formData, setFormData] = useState({
    uid: '',
    datevisit: '',
    time: '',
    complaint: '',
    treatment: '',
    medicine: '',
    additional: '',
    follow: '',
    reference: '',
    fee: '',
    balance: '',
    credit: '0',
    payment_mode: ''
  });

  useEffect(() => {
    fetchVisits();
    fetchPatients();
  }, [page, rowsPerPage, searchTerm]);

  const fetchVisits = async () => {
    try {
      setLoading(true);
      const response = await api.getVisits({
        page: page + 1,
        limit: rowsPerPage,
        sortBy: 'datevisit',
        sortOrder: 'DESC'
      });
      setVisits(response.data?.visits || []);
      setTotalVisits(response.data?.pagination?.totalItems || 0);
    } catch (error) {
      console.error('Error fetching visits:', error);
      setSnackbar({ open: true, message: 'Failed to fetch visits', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await api.getPatients({ limit: 1000 });
      setPatients(response.data?.patients || []);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingVisit) {
        await api.updateVisit(editingVisit.id, formData);
        setSnackbar({ open: true, message: 'Visit updated successfully', severity: 'success' });
      } else {
        await api.createVisit(formData);
        setSnackbar({ open: true, message: 'Visit added successfully', severity: 'success' });
      }
      setOpenDialog(false);
      resetForm();
      fetchVisits();
    } catch (error) {
      console.error('Error saving visit:', error);
      setSnackbar({ open: true, message: 'Failed to save visit', severity: 'error' });
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this visit? This will hide it from the system but keep the data for record keeping.')) {
      try {
        await api.deleteVisit(id);
        setSnackbar({ open: true, message: 'Visit deleted successfully', severity: 'success' });
        fetchVisits();
      } catch (error) {
        console.error('Error deleting visit:', error);
        setSnackbar({ open: true, message: 'Failed to delete visit', severity: 'error' });
      }
    }
  };

  const handleEdit = (visit: Visit) => {
    setEditingVisit(visit);
    setFormData({
      uid: visit.uid.toString(),
      datevisit: visit.datevisit,
      time: visit.time,
      complaint: visit.complaint,
      treatment: visit.treatment,
      medicine: visit.medicine,
      additional: visit.additional || '',
      follow: visit.follow ? (typeof visit.follow === 'string' ? visit.follow : new Date(visit.follow).toISOString().split('T')[0]) : '',
      reference: visit.reference || '',
      fee: visit.fee,
      balance: visit.balance,
      credit: visit.credit || '0',
      payment_mode: visit.payment_mode
    });
    setOpenDialog(true);
  };

  const resetForm = () => {
    setFormData({
      uid: '',
      datevisit: '',
      time: '',
      complaint: '',
      treatment: '',
      medicine: '',
      additional: '',
      follow: '',
      reference: '',
      fee: '',
      balance: '',
      credit: '0',
      payment_mode: ''
    });
    setEditingVisit(null);
  };

  const handleAddNew = () => {
    resetForm();
    setOpenDialog(true);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatCurrency = (amount: string) => {
    const num = parseFloat(amount || '0');
    return `₹${num % 1 === 0 ? num.toFixed(0) : num.toFixed(2)}`;
  };

  const getPatientName = (uid: number) => {
    const patient = patients.find(p => p.id === Number(uid));
    return patient ? `${patient.name} (${patient.opd})` : 'Unknown Patient';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Visits
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
        >
          Add Visit
        </Button>
      </Box>

      {/* Search Bar */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'center' }}>
            <Box sx={{ flex: 1, width: '100%' }}>
              <TextField
                fullWidth
                placeholder="Search visits..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                Total: {totalVisits} visits
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Visits Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600, overflowX: 'auto' }}>
          <Table stickyHeader sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow>
                <TableCell>Patient</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Complaint</TableCell>
                <TableCell>Treatment</TableCell>
                <TableCell>Follow Up</TableCell>
                <TableCell>Fee</TableCell>
                <TableCell>Balance</TableCell>
                <TableCell sx={{ minWidth: 120, position: 'sticky', right: 0, backgroundColor: 'white', zIndex: 1 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : visits.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No visits found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visits.map((visit) => (
                  <TableRow key={visit.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {getPatientName(Number(visit.uid))}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(visit.datevisit)}</TableCell>
                    <TableCell>{visit.time}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {visit.complaint}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {visit.treatment}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {visit.follow ? (() => {
                        const followDate = typeof visit.follow === 'string' 
                          ? new Date(visit.follow) 
                          : new Date(visit.follow);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        followDate.setHours(0, 0, 0, 0);
                        
                        // Check if patient has visited after the follow-up date
                        const hasVisitedAfterFollowUp = visits.some(otherVisit => {
                          if (otherVisit.id === visit.id) return false; // Don't compare with same visit
                          const visitDate = new Date(otherVisit.datevisit);
                          visitDate.setHours(0, 0, 0, 0);
                          return visitDate > followDate;
                        });
                        
                        // If patient has visited after follow-up date, show like date of visit
                        if (hasVisitedAfterFollowUp) {
                          return (
                            <Typography variant="body2">
                              {formatDate(`${followDate.getFullYear()}-${String(followDate.getMonth() + 1).padStart(2, '0')}-${String(followDate.getDate()).padStart(2, '0')}`)}
                            </Typography>
                          );
                        }
                        
                        const daysDiff = Math.ceil((followDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                        
                        let chipColor: 'success' | 'warning' | 'error' | 'default';
                        if (daysDiff >= 0) {
                          chipColor = 'success'; // Today or future - green
                        } else if (daysDiff >= -3) {
                          chipColor = 'warning'; // 1-3 days past - yellow
                        } else {
                          chipColor = 'error'; // More than 3 days past - red
                        }
                        
                        return (
                          <Chip 
                            label={formatDate(`${followDate.getFullYear()}-${String(followDate.getMonth() + 1).padStart(2, '0')}-${String(followDate.getDate()).padStart(2, '0')}`)} 
                            size="small" 
                            color={chipColor}
                          />
                        );
                      })() : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          No follow-up
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatCurrency(visit.fee)}
                        size="small"
                        color="success"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatCurrency(visit.balance)}
                        size="small"
                        color={parseFloat(visit.balance) > 0 ? 'warning' : 'success'}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 120, position: 'sticky', right: 0, backgroundColor: 'white', zIndex: 1 }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/visits/${visit.id}`)}
                          color="info"
                        >
                          <ViewIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(visit)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(visit.id)}
                          color="error"
                          title="Delete Visit"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalVisits}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingVisit ? 'Edit Visit' : 'Add New Visit'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Autocomplete
                  options={patients}
                  getOptionLabel={(option) => `${option.name} (${option.opd})`}
                  value={patients.find(p => p.id.toString() === formData.uid) || null}
                  onChange={(_, newValue) => setFormData({ ...formData, uid: newValue?.id.toString() || '' })}
                  renderInput={(params) => (
                    <TextField {...params} label="Patient" required />
                  )}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  label="Visit Date"
                  type="date"
                  value={formData.datevisit}
                  onChange={(e) => setFormData({ ...formData, datevisit: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  label="Time"
                  type="time"
                  value={
                    formData.time
                      ? (() => {
                          // Convert 24-hour time to 12-hour format with am/pm for display
                          const [hourStr, minute] = formData.time.split(':');
                          let hour = parseInt(hourStr, 10);
                          let ampm = 'AM';
                          if (hour === 0) {
                            hour = 12;
                          } else if (hour === 12) {
                            ampm = 'PM';
                          } else if (hour > 12) {
                            hour -= 12;
                            ampm = 'PM';
                          }
                          return `${hour.toString().padStart(2, '0')}:${minute} ${ampm}`;
                        })()
                      : ''
                  }
                  onChange={(e) => {
                    let value = e.target.value;
                    // Accept both 12-hour and 24-hour input, convert to 12-hour with am/pm
                    let hour = 0, minute = '00', ampm = 'AM';
                    if (value.includes('AM') || value.includes('PM')) {
                      // Already in 12-hour format
                      const match = value.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
                      if (match) {
                        hour = parseInt(match[1], 10);
                        minute = match[2];
                        ampm = match[3].toUpperCase();
                      }
                    } else if (value.includes(':')) {
                      // 24-hour format, convert to 12-hour
                      const [h, m] = value.split(':');
                      hour = parseInt(h, 10);
                      minute = m;
                      if (hour === 0) {
                        hour = 12;
                        ampm = 'AM';
                      } else if (hour === 12) {
                        ampm = 'PM';
                      } else if (hour > 12) {
                        hour -= 12;
                        ampm = 'PM';
                      }
                    }
                    const formatted = `${hour.toString().padStart(2, '0')}:${minute} ${ampm}`;
                    setFormData({ ...formData, time: formatted });
                  }}
                  placeholder="hh:mm AM/PM"
                  inputProps={{
                    pattern: "(0[1-9]|1[0-2]):[0-5][0-9] (AM|PM)",
                  }}
                  required
                  helperText="Format: hh:mm AM/PM"
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <FormControl fullWidth required>
                  <InputLabel>Payment Mode</InputLabel>
                  <Select
                    value={formData.payment_mode}
                    label="Payment Mode"
                    onChange={(e) => setFormData({ ...formData, payment_mode: e.target.value })}
                  >
                    <MenuItem value="Cash">Cash</MenuItem>
                    <MenuItem value="Card">Card</MenuItem>
                    <MenuItem value="UPI">UPI</MenuItem>
                    <MenuItem value="Cheque">Cheque</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
            <Box>
              <TextField
                fullWidth
                label="Complaint"
                value={formData.complaint}
                onChange={(e) => setFormData({ ...formData, complaint: e.target.value })}
                multiline
                rows={2}
                required
              />
            </Box>
            <Box>
              <TextField
                fullWidth
                label="Treatment"
                value={formData.treatment}
                onChange={(e) => setFormData({ ...formData, treatment: e.target.value })}
                multiline
                rows={2}
                required
              />
            </Box>
            <Box>
              <TextField
                fullWidth
                label="Medicine"
                value={formData.medicine}
                onChange={(e) => setFormData({ ...formData, medicine: e.target.value })}
                multiline
                rows={2}
                required
              />
            </Box>
            <Box>
              <TextField
                fullWidth
                label="Additional Notes"
                value={formData.additional}
                onChange={(e) => setFormData({ ...formData, additional: e.target.value })}
                multiline
                rows={2}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  label="Follow Up"
                  value={formData.follow}
                  onChange={(e) => setFormData({ ...formData, follow: e.target.value })}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  label="Reference"
                  value={formData.reference}
                  onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  label="Fee"
                  type="number"
                  value={formData.fee}
                  onChange={(e) => setFormData({ ...formData, fee: e.target.value })}
                  required
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  label="Balance"
                  type="number"
                  value={formData.balance}
                  onChange={(e) => setFormData({ ...formData, balance: e.target.value })}
                  required
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  label="Credit"
                  type="number"
                  value={formData.credit}
                  onChange={(e) => setFormData({ ...formData, credit: e.target.value })}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingVisit ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Visits;